import type { BaseOptionType } from "antd/es/select"

export const UNRESOLVED_OPTION_LABEL = "Unavailable"

type OptionValue = string | number

export type FallbackLabels =
  | Record<string, string>
  | Map<string, string>
  | undefined

function readFallback(
  labels: FallbackLabels,
  value: OptionValue
): string | undefined {
  if (!labels) return undefined
  const key = String(value)
  return labels instanceof Map ? labels.get(key) : labels[key]
}

export function toSelectedValues(selected: unknown): OptionValue[] {
  const list = Array.isArray(selected) ? selected : [selected]

  return list.flatMap((entry) => {
    if (typeof entry === "string" || typeof entry === "number") return [entry]
    // labelInValue and record-shaped values ({ id }, { value }) are both in use.
    if (entry && typeof entry === "object") {
      const candidate =
        (entry as { value?: unknown; id?: unknown }).value ??
        (entry as { id?: unknown }).id
      if (typeof candidate === "string" || typeof candidate === "number") {
        return [candidate]
      }
    }
    return []
  })
}

/**
 * antd renders the raw value whenever a selected entry has no matching option,
 * which leaks ids into the UI. Options lists are routinely narrower than the set
 * of already-selected values (server-side scoping, pagination, retired lookups),
 * so every selected value gets an option — labelled from `labels` when the
 * record carries a name, otherwise by a neutral placeholder.
 *
 * Synthesized options are deliberately NOT disabled: antd drops the remove icon
 * from a disabled tag, which would make the value impossible to clear.
 *
 * Free-text modes ("tags", "combobox") are exempt: there a value outside the
 * option list is the user's own input and its label IS the value.
 */
export function modeAcceptsFreeText(mode?: string): boolean {
  return mode === "tags" || mode === "combobox"
}

export function mergeSelectedIntoOptions(
  options: BaseOptionType[] | undefined,
  selected: unknown,
  labels?: FallbackLabels,
  fallbackLabel: string = UNRESOLVED_OPTION_LABEL
): BaseOptionType[] {
  const base = options ?? []
  const values = toSelectedValues(selected)
  if (!values.length) return base

  // An option list may be grouped, and a grouped entry carries its values one
  // level down; reading only the top level would re-synthesize every option
  // inside a group as unresolved.
  const present = new Set(
    base.flatMap((option) =>
      Array.isArray(option?.options)
        ? (option.options as BaseOptionType[]).map((child) =>
            String(child?.value)
          )
        : [String(option?.value)]
    )
  )
  const seen = new Set<string>()

  const missing = values.filter((value) => {
    const key = String(value)
    if (present.has(key) || seen.has(key)) return false
    seen.add(key)
    return true
  })

  if (!missing.length) return base

  return [
    ...base,
    ...missing.map((value) => ({
      value,
      label: readFallback(labels, value) ?? fallbackLabel
    }))
  ]
}
